import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import PageLayout from "../components/layout/PageLayout";
import api, { formatApiError } from "../lib/api";

const STEPS = [
    { key: "personal", title: "Personal Info", fields: [
        { name: "full_name", label: "Full Legal Name", required: true },
        { name: "preferred_name", label: "Preferred Name" },
        { name: "dob", label: "Date of Birth", type: "date" },
        { name: "email", label: "Email", type: "email", required: true },
        { name: "phone", label: "Phone", type: "tel", required: true },
        { name: "city", label: "City" },
        { name: "state", label: "State" },
        { name: "guardian_name", label: "Parent / Guardian Name" },
        { name: "guardian_phone", label: "Parent / Guardian Phone" },
        { name: "contact_pref", label: "Preferred Contact", type: "select", options: ["Email","Phone","Text"] },
    ]},
    { key: "athletic", title: "Athletic Profile", fields: [
        { name: "school", label: "School", required: true },
        { name: "sport", label: "Sport", type: "select", options: ["Football","Basketball"], required: true },
        { name: "team", label: "Team" },
        { name: "position", label: "Position" },
        { name: "jersey", label: "Jersey #" },
        { name: "height", label: "Height" },
        { name: "weight", label: "Weight" },
        { name: "grad_year", label: "Graduation Year" },
        { name: "eligibility", label: "Eligibility Year" },
        { name: "conference", label: "Conference" },
        { name: "stats", label: "Athletic Statistics", type: "textarea" },
        { name: "awards", label: "Awards / Rankings / Honors", type: "textarea" },
        { name: "academics", label: "Academic Achievements", type: "textarea" },
        { name: "coach", label: "Coach Name & Contact" },
        { name: "highlight_url", label: "Highlight Reel URL" },
    ]},
    { key: "brand", title: "Brand Profile", fields: [
        { name: "bio", label: "Biography", type: "textarea", required: true },
        { name: "story", label: "Personal Story", type: "textarea" },
        { name: "interests", label: "Interests Outside Sports" },
        { name: "causes", label: "Causes You Support" },
        { name: "goals", label: "Career Goals", type: "textarea" },
        { name: "favorite_brands", label: "Favorite Brands" },
        { name: "desired_categories", label: "Desired Sponsorship Categories" },
        { name: "will_not_promote", label: "Categories You WILL NOT Promote" },
        { name: "community", label: "Community Involvement", type: "textarea" },
        { name: "speaking", label: "Speaking / Camp / Merch Interests" },
    ]},
    { key: "social", title: "Social Media", fields: [
        { name: "instagram", label: "Instagram" },
        { name: "tiktok", label: "TikTok" },
        { name: "youtube", label: "YouTube" },
        { name: "x", label: "X (Twitter)" },
        { name: "facebook", label: "Facebook" },
        { name: "linkedin", label: "LinkedIn" },
        { name: "twitch", label: "Twitch" },
        { name: "snapchat", label: "Snapchat" },
        { name: "followers", label: "Total Follower Count", type: "number" },
        { name: "engagement", label: "Average Engagement %" },
        { name: "top_content", label: "Most Successful Content", type: "textarea" },
        { name: "posting_freq", label: "Current Posting Frequency" },
    ]},
    { key: "media", title: "Media Uploads (URLs)", desc: "For MVP we accept URLs (Google Drive, Dropbox, YouTube). Native uploads coming soon.", fields: [
        { name: "headshot_url", label: "Headshot URL" },
        { name: "action_url", label: "Action Photograph URL" },
        { name: "lifestyle_url", label: "Lifestyle Photograph URL" },
        { name: "highlight_url", label: "Highlight Video URL" },
        { name: "interview_url", label: "Interview URL" },
        { name: "press_url", label: "Press Article URL" },
        { name: "resume_url", label: "Athletic Résumé URL" },
        { name: "media_kit_url", label: "Media Kit URL" },
        { name: "existing_nil_url", label: "Existing NIL Agreements URL" },
    ]},
    { key: "representation", title: "Representation", fields: [
        { name: "currently_represented", label: "Currently Represented?", type: "select", options: ["No","Yes"] },
        { name: "previous_nil", label: "Previous NIL Deals?", type: "textarea" },
        { name: "income_goals", label: "Income Goals" },
        { name: "help_needed", label: "What Help Do You Need Most?", type: "textarea" },
        { name: "why_db", label: "Why Digital Beast?", type: "textarea", required: true },
        { name: "how_found", label: "How Did You Hear About Us?" },
        { name: "auth_agreement", label: "Authorized to Enter Agreement?", type: "select", options: ["Yes","No"] },
        { name: "parent_approval", label: "Parent Approval Required?", type: "select", options: ["No","Yes"] },
        { name: "school_approval", label: "School Approval Required?", type: "select", options: ["No","Yes"] },
    ]},
];

function Field({ f, value, onChange }) {
    if (f.type === "textarea") return (
        <div>
            <label className="db-label">{f.label}{f.required && <span className="text-[#d4af37]"> *</span>}</label>
            <textarea className="db-input-block db-textarea" value={value || ""} onChange={e => onChange(f.name, e.target.value)}
                data-testid={`field-${f.name}`} />
        </div>
    );
    if (f.type === "select") return (
        <div>
            <label className="db-label">{f.label}{f.required && <span className="text-[#d4af37]"> *</span>}</label>
            <select className="db-input-block" value={value || ""} onChange={e => onChange(f.name, e.target.value)} data-testid={`field-${f.name}`}>
                <option value="">Select…</option>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
    return (
        <div>
            <label className="db-label">{f.label}{f.required && <span className="text-[#d4af37]"> *</span>}</label>
            <input type={f.type || "text"} className="db-input-block" value={value || ""} onChange={e => onChange(f.name, e.target.value)} data-testid={`field-${f.name}`} />
        </div>
    );
}

export default function Apply() {
    const [step, setStep] = useState(0);
    const [data, setData] = useState({});
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submittedId, setSubmittedId] = useState(null);

    const total = STEPS.length;
    const cur = STEPS[step];
    const stepData = data[cur.key] || {};
    const setField = (name, value) => setData(d => ({ ...d, [cur.key]: { ...(d[cur.key] || {}), [name]: value } }));

    const next = () => {
        // simple required validation
        const missing = cur.fields.filter(f => f.required && !stepData[f.name]);
        if (missing.length) {
            toast.error("Required: " + missing.map(m => m.label).join(", "));
            return;
        }
        if (step < total - 1) setStep(step + 1);
    };
    const prev = () => step > 0 && setStep(step - 1);

    const submit = async () => {
        if (!consent) { toast.error("Please accept the consent to submit."); return; }
        setSubmitting(true);
        try {
            const { data: res } = await api.post("/applications", { ...data, consent: true });
            setSubmittedId(res.id);
            toast.success("Application submitted! Our team will be in touch.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (submittedId) {
        return (
            <PageLayout>
                <section className="pt-40 pb-32">
                    <div className="max-w-2xl mx-auto px-5 md:px-10 text-center" data-testid="apply-success">
                        <div className="w-20 h-20 border-2 border-[#d4af37] flex items-center justify-center mx-auto">
                            <Check size={32} className="text-[#d4af37]" />
                        </div>
                        <h1 className="font-display text-4xl md:text-6xl text-white mt-8 tracking-tight">WELCOME<br/>TO THE PACK.</h1>
                        <p className="text-white/60 mt-6 leading-relaxed">
                            Your application has been received. A member of the Digital Beast team will review it within 5 business days and reach out with next steps.
                        </p>
                        <div className="text-white/40 text-xs mt-8 font-mono">Reference #{submittedId}</div>
                    </div>
                </section>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <section className="pt-32 pb-10 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Athlete Application</div>
                    <h1 className="font-display text-4xl md:text-6xl text-white leading-none tracking-tight" data-testid="apply-heading">
                        APPLY FOR <span className="text-[#d4af37]">REPRESENTATION</span>
                    </h1>
                    <p className="text-white/60 mt-4 max-w-2xl">Six quick steps. Everything is confidential. Save-and-continue coming in Phase 2.</p>
                </div>
            </section>

            <section className="py-14">
                <div className="max-w-3xl mx-auto px-5 md:px-10">
                    {/* progress */}
                    <div className="mb-10">
                        <div className="flex justify-between mb-3">
                            <div className="text-xs uppercase tracking-[0.28em] text-white/50">Step {step + 1} / {total}</div>
                            <div className="text-xs uppercase tracking-[0.28em] text-[#d4af37]">{cur.title}</div>
                        </div>
                        <div className="progress-track"><div className="progress-fill" style={{width: `${((step + 1) / total) * 100}%`}} /></div>
                    </div>

                    <div className="space-y-5" data-testid={`step-${cur.key}`}>
                        <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight">{cur.title}</h2>
                        {cur.desc && <p className="text-white/50 text-sm">{cur.desc}</p>}
                        <div className="grid md:grid-cols-2 gap-5 mt-4">
                            {cur.fields.map(f => (
                                <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                                    <Field f={f} value={stepData[f.name]} onChange={setField} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {step === total - 1 && (
                        <label className="mt-8 flex items-start gap-3 cursor-pointer" data-testid="apply-consent-label">
                            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                                className="mt-1 accent-[#d4af37]" data-testid="apply-consent-checkbox" />
                            <span className="text-white/70 text-sm leading-relaxed">
                                I authorize Digital Beast to contact me regarding representation. I understand Digital Beast does not guarantee scholarships, roster placement, playing time, professional contracts, endorsements or NIL income. I confirm all information provided is accurate.
                            </span>
                        </label>
                    )}

                    <div className="mt-10 flex justify-between items-center border-t border-white/10 pt-6">
                        <button onClick={prev} disabled={step === 0} className="btn-ghost flex items-center gap-2 disabled:opacity-30" data-testid="apply-prev-btn">
                            <ArrowLeft size={14} /> Back
                        </button>
                        {step < total - 1 ? (
                            <button onClick={next} className="btn-gold" data-testid="apply-next-btn">Next <ArrowRight size={14} /></button>
                        ) : (
                            <button onClick={submit} disabled={submitting} className="btn-gold" data-testid="apply-submit-btn">
                                {submitting ? "Submitting…" : "Submit Application"} <Check size={14} />
                            </button>
                        )}
                    </div>
                </div>
            </section>
        </PageLayout>
    );
}
